/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * Collapsible sections, as used by the glossary in the table of contents.
 *
 * Replaces jQuery UI's accordion, the last of its widgets in the Map
 * Viewer.
 * The markup it is given is a flat list where headings and panels
 * alternate — `header: '> li:even'` in the old configuration — which is
 * why the pairing below walks the list two at a time.
 *
 * Each heading becomes a real `<button>` rather than a `<li>` with a click
 * handler: a button is reachable by keyboard, announces itself as a
 * control, and carries `aria-expanded` without any help.
 *
 * The list, its headings and its panels are classed the way the widget
 * this replaced classed them, because the stylesheet selects on those
 * positions -- the marker on a heading is a `::before` on the `<li>`, not
 * on the button inside it. The state class is redundant with
 * `aria-expanded` and is set in the same place, so the two cannot drift.
 */

let counter = 0;

/**
 * Make a list of alternating headings and panels collapsible.
 *
 * @param {HTMLElement} list - The list element.
 * @param {object} [options]
 * @param {boolean} [options.collapsible] - Allow closing the open section.
 * @param {number} [options.active] - Index to open initially; -1 for none.
 * @returns {object|null} A controller, or nothing if there is nothing to
 *   do or the list is already an accordion.
 */
export function createAccordion(list, options = {}) {
    const { collapsible = true, active = -1 } = options;
    if (!list) return null;

    // Initialising twice would move each heading's content into a second
    // button and re-pair the sections against the changed markup, so the
    // widget refuses rather than leaving that to every caller.
    if (list.dataset.accordion === 'ready') return null;

    const children = [...list.children];
    const sections = [];
    for (let i = 0; i + 1 < children.length; i += 2) {
        sections.push({ heading: children[i], panel: children[i + 1] });
    }
    if (!sections.length) return null;

    const id = (counter += 1);
    list.classList.add('accordion');
    list.dataset.accordion = 'ready';

    sections.forEach(({ heading, panel }, index) => {
        heading.classList.add('accordion-header');
        panel.classList.add('accordion-content');

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'accordion-toggle';
        button.id = `accordion-${id}-header-${index}`;
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', `accordion-${id}-panel-${index}`);
        // Move the heading's own content into the button so its text
        // becomes the control's accessible name.
        button.append(...heading.childNodes);
        heading.appendChild(button);

        panel.id = `accordion-${id}-panel-${index}`;
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', button.id);
        // `hidden`, not `opacity`: a panel faded out is still in the tab
        // order and still read aloud.
        panel.hidden = true;

        button.addEventListener('click', () => toggle(index));
    });

    function toggle(index) {
        const isOpen = !sections[index].panel.hidden;
        if (isOpen && !collapsible) return;
        sections.forEach(({ heading, panel }, i) => {
            const open = i === index && !isOpen;
            panel.hidden = !open;
            heading.classList.toggle('accordion-header-active', open);
            heading
                .querySelector('.accordion-toggle')
                ?.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
    }

    if (active >= 0 && active < sections.length) {
        toggle(active);
    }

    return {
        /** Open one section, closing whichever was open. */
        open: (index) => {
            if (sections[index]?.panel.hidden) toggle(index);
        },
        /** Close every section. */
        closeAll: () => {
            sections.forEach(({ heading, panel }) => {
                panel.hidden = true;
                heading.classList.remove('accordion-header-active');
                heading
                    .querySelector('.accordion-toggle')
                    ?.setAttribute('aria-expanded', 'false');
            });
        },
        /** How many sections it found. */
        get length() {
            return sections.length;
        },
    };
}

export default createAccordion;
